import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

interface Props {
  /** `HH:mm`, or null when unset. */
  value: string | null;
  onChange: (next: string) => void;
  label: string;
  disabled?: boolean;
}

/**
 * `HH:mm` picker with finite hour (00–23) and minute (00–59) columns. Replaces
 * the browser's time picker, whose columns wrap around endlessly and cannot be
 * configured. Picking a minute closes it; Escape or a click outside too.
 */
export function TimePicker({ value, onChange, label, disabled = false }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const [hour, minute] = value ? value.split(':') : [null, null];

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Close only this picker, not the filter popover around it
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div className="time-picker" ref={rootRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        className="column-filter-input time-picker-trigger"
        aria-label={label}
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen(!open)}
      >
        {value ?? '--:--'}
      </button>
      {open && (
        <div className="time-picker-dropdown">
          <TimeColumn
            label="Hours"
            options={HOURS}
            selected={hour}
            onSelect={(h) => onChange(`${h}:${minute ?? '00'}`)}
          />
          <TimeColumn
            label="Minutes"
            options={MINUTES}
            selected={minute}
            onSelect={(m) => {
              onChange(`${hour ?? '00'}:${m}`);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

function TimeColumn({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string;
  options: string[];
  selected: string | null;
  onSelect: (value: string) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  // Open centred on the current value. scrollTop, not scrollIntoView, so the
  // popover and the page around it do not scroll too.
  useEffect(() => {
    const list = listRef.current;
    const current = list?.querySelector<HTMLElement>('.time-picker-option.selected');
    if (list && current) {
      list.scrollTop = current.offsetTop - (list.clientHeight - current.offsetHeight) / 2;
    }
  }, []);

  return (
    <div className="time-picker-column" ref={listRef} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option}
          type="button"
          className={`time-picker-option ${option === selected ? 'selected' : ''}`}
          aria-pressed={option === selected}
          onClick={() => onSelect(option)}
        >
          {option}
        </button>
      ))}
    </div>
  );
}
